/** Media play URL helpers for chat lightbox / bubbles. */

export type MediaPlayFields = {
  message_content?: string;
  /** Preferred file URL when content is caption */
  media_url?: string | null;
  media_hls?: string | null;
  media_hls_tier?: number | null;
  media_mp4_1080?: string | null;
  media_mp4_720?: string | null;
  media_process_status?: string | null;
  message_type?: string;
  video_size_bytes?: number | null;
  videoSizeBytes?: number | null;
};

export function normalizeMediaUrl(url?: string | null): string {
  if (!url) return "";
  return String(url).replace(/\\/g, "/");
}

export function isHlsUrl(url?: string | null): boolean {
  if (!url) return false;
  const path = normalizeMediaUrl(url).split(/[?#]/)[0] || "";
  return /\.m3u8$/i.test(path) || /\/api\/media\/s3\/.+\.m3u8$/i.test(path);
}

/**
 * storage.mwsapis.ru/.../lyke/... → /api/media/s3/lyke/...
 */
export function toSameOriginMediaUrl(url?: string | null): string {
  const raw = normalizeMediaUrl(url);
  if (!raw) return "";
  if (/\/api\/media\/s3\//i.test(raw)) return raw;

  let key = "";
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      let p = u.pathname.replace(/^\/+/, "");
      if (p.startsWith("lyke/lyke/")) p = p.slice("lyke/".length);
      else if (!p.startsWith("lyke/") && /(?:^|\/)lyke\//.test(p)) {
        p = p.slice(p.indexOf("lyke/"));
      }
      if (p.startsWith("lyke/")) key = p;
    } catch {
      return raw;
    }
  } else if (raw.startsWith("lyke/")) {
    key = raw.replace(/^\/+/, "");
  }

  if (!key) return raw;
  const base = (import.meta.env.VITE_BASE_URL || "").replace(/\/+$/, "");
  const encoded = key
    .split("/")
    .map((seg) => encodeURIComponent(seg))
    .join("/");
  return `${base}/api/media/s3/${encoded}`;
}

function hlsReady(message: MediaPlayFields): boolean {
  const hls = normalizeMediaUrl(message.media_hls);
  const status = String(message.media_process_status || "");
  if (!hls) return false;
  return status === "COMPLETED" || (status === "" && isHlsUrl(hls));
}

/** Best progressive remux for bubble autoplay / fallbacks. */
export function getMessageRemuxUrl(message: MediaPlayFields): string {
  const mp41080 = normalizeMediaUrl(message.media_mp4_1080);
  const mp4720 = normalizeMediaUrl(message.media_mp4_720);
  const tier = Number(message.media_hls_tier);
  if (tier === 1080 && mp41080) return mp41080;
  if (tier === 720 && mp4720) return mp4720;
  if (mp41080) return mp41080;
  if (mp4720) return mp4720;
  return "";
}

/**
 * Lightbox / click: HLS first when ready, else remux, else source.
 */
function looksLikeMediaPathOrUrl(value?: string | null): boolean {
  const s = String(value || "").trim();
  if (!s) return false;
  if (/^https?:\/\//i.test(s)) return true;
  if (s.startsWith("uploads/") || s.startsWith("lyke/")) return true;
  if (/\.(webp|jpe?g|png|gif|mp4|mov|webm|m3u8)(\?|$)/i.test(s)) return true;
  return false;
}

/** File URL only — never caption text from message_content. */
function sourceMediaUrl(message: MediaPlayFields): string {
  const mediaUrl = normalizeMediaUrl(message.media_url);
  if (mediaUrl) return mediaUrl;
  const content = normalizeMediaUrl(message.message_content);
  if (looksLikeMediaPathOrUrl(content)) return content;
  return "";
}

export function getMessagePlayUrl(message: MediaPlayFields): string {
  if (hlsReady(message)) {
    return normalizeMediaUrl(message.media_hls);
  }
  const remux = getMessageRemuxUrl(message);
  if (remux) return remux;
  return sourceMediaUrl(message);
}

/** Inline bubble autoplay prefers progressive remux (simpler than HLS MSE). */
export function getBubbleAutoplayUrl(message: MediaPlayFields): string {
  const remux = getMessageRemuxUrl(message);
  if (remux) return remux;
  if (hlsReady(message)) return normalizeMediaUrl(message.media_hls);
  return sourceMediaUrl(message);
}

export function getVideoSizeBytes(message: MediaPlayFields): number {
  const n = Number(message.video_size_bytes ?? message.videoSizeBytes);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Autoplay in bubble — same rules for plain video and video+caption.
 * Prefer remux/HLS when ready; else source under 20 MiB.
 * Never depends on caption / message_content text.
 */
export function shouldAutoplayInBubble(message: MediaPlayFields): boolean {
  if (getMessageRemuxUrl(message) || hlsReady(message)) return true;
  const bytes = getVideoSizeBytes(message);
  if (!(bytes > 0)) return false;
  return bytes < 20 * 1024 * 1024;
}

/**
 * Asset folder from HLS or remux/source key.
 * Supports legacy …/hls/ and …/hls_720/|hls_1080/.
 */
export function assetBaseFromMediaUrl(url: string): string {
  let publicUrl = normalizeMediaUrl(url);
  if (!publicUrl) return "";

  const proxyIdx = publicUrl.indexOf("/api/media/s3/");
  if (proxyIdx >= 0) {
    const key = decodeURIComponent(
      publicUrl.slice(proxyIdx + "/api/media/s3/".length).split(/[?#]/)[0],
    );
    publicUrl = `https://storage.mwsapis.ru/lyke/${key}`;
  }

  let base = publicUrl
    .replace(/\/hls(?:_720|_1080)?\/[^/]*\.m3u8$/i, "")
    .replace(/\/media_mp4_(?:720|1080)\.mp4$/i, "")
    .replace(/\/playback\.(?:mp4|webm)$/i, "")
    .replace(/\/source\.(?:mp4|mov|webm)$/i, "");

  if (!base || base === publicUrl) {
    // strip last file segment if under /video/{id}/
    const m = publicUrl.match(/^(https?:\/\/.+\/lyke\/chat\/[^/]+\/video\/[^/]+)\//i);
    if (m) return m[1];
    return "";
  }
  return base.replace(/\/+$/, "");
}

/** @deprecated use assetBaseFromMediaUrl */
function assetBaseFromHls(hlsUrl: string): string {
  return assetBaseFromMediaUrl(hlsUrl);
}

export function isYaBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /YaBrowser|YaSearchBrowser|Yowser/i.test(navigator.userAgent || "");
}

/**
 * Progressive fallbacks after HLS fail.
 * Priority: remux (media_mp4_*) → webm → playback.mp4 → source
 */
export function getMessageProgressiveFallbacks(
  message: MediaPlayFields,
): string[] {
  const out: string[] = [];
  const push = (u?: string | null) => {
    const n = normalizeMediaUrl(u);
    if (n && !out.includes(n)) out.push(n);
  };

  const remux = getMessageRemuxUrl(message);
  push(remux);
  push(message.media_mp4_1080);
  push(message.media_mp4_720);

  const base =
    assetBaseFromMediaUrl(message.media_hls || "") ||
    assetBaseFromMediaUrl(remux) ||
    assetBaseFromMediaUrl(message.message_content || "");

  if (base) {
    const webm = `${base}/playback.webm`;
    const mp4 = `${base}/playback.mp4`;
    const sources = [`${base}/source.mp4`, `${base}/source.mov`];
    if (isYaBrowser()) {
      push(mp4);
      push(webm);
      sources.forEach(push);
    } else {
      push(webm);
      push(mp4);
      sources.forEach(push);
    }
  }

  push(message.message_content);
  return out;
}

/**
 * Infer progressive URLs from HLS path alone (lightbox only has URL string).
 */
export function inferSourceFallbacksFromHls(hlsUrl: string): string[] {
  const base = assetBaseFromHls(hlsUrl);
  if (!base) return [];
  const mp41080 = `${base}/media_mp4_1080.mp4`;
  const mp4720 = `${base}/media_mp4_720.mp4`;
  const webm = `${base}/playback.webm`;
  const mp4 = `${base}/playback.mp4`;
  const sources = [`${base}/source.mp4`, `${base}/source.mov`];
  // Prefer remux over playback.*
  if (isYaBrowser()) {
    return [mp41080, mp4720, mp4, webm, ...sources];
  }
  return [mp41080, mp4720, webm, mp4, ...sources];
}

export function inferWebmFromHls(hlsUrl: string): string | null {
  const base = assetBaseFromHls(hlsUrl);
  return base ? `${base}/playback.webm` : null;
}

/** Always HLS first when available; always same-origin proxy for MSE/XHR. */
export function resolveLightboxPlayUrl(
  preferred: string,
): { url: string; via: "hls" | "direct" } {
  const url = normalizeMediaUrl(preferred);
  const proxied = toSameOriginMediaUrl(url) || url;
  if (!isHlsUrl(url)) {
    return { url: proxied, via: "direct" };
  }
  return { url: proxied, via: "hls" };
}

export type SimpleLog = (message: string) => void;

function clearVideoSource(video: HTMLVideoElement) {
  try {
    video.pause();
  } catch {
    /* */
  }
  video.removeAttribute("src");
  video.srcObject = null;
  try {
    video.load();
  } catch {
    /* */
  }
}

/** Same-origin progressive via native &lt;video src&gt; (preferred over blob after MSE). */
export async function loadVideoDirect(
  video: HTMLVideoElement,
  url: string,
  onLog?: SimpleLog,
): Promise<{ mode: "direct" } | { error: string }> {
  const playUrl = toSameOriginMediaUrl(url) || url;
  const log = onLog || (() => {});
  log("Загрузка progressive (direct)…");
  clearVideoSource(video);
  video.src = playUrl;
  video.load();
  try {
    video.muted = true;
    void video.play().catch(() => {});
  } catch {
    /* */
  }
  return { mode: "direct" };
}

export async function loadVideoViaBlob(
  video: HTMLVideoElement,
  url: string,
  onLog?: SimpleLog,
  timeoutMs = 45000,
): Promise<{ objectUrl: string } | { error: string }> {
  const playUrl = toSameOriginMediaUrl(url) || url;
  const log = onLog || (() => {});
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), timeoutMs);

  try {
    log("Загрузка progressive (blob)…");
    const res = await fetch(playUrl, {
      method: "GET",
      signal: ctrl.signal,
      credentials: "same-origin",
      cache: "default",
    });
    if (!res.ok) {
      log("Progressive недоступен");
      return { error: `HTTP ${res.status}` };
    }
    const blob = await res.blob();
    if (!blob.size) {
      log("Пустой progressive");
      return { error: "empty blob" };
    }
    const typed =
      blob.type && blob.type.startsWith("video/")
        ? blob
        : new Blob([blob], {
            type: /\.webm(\?|$)/i.test(playUrl)
              ? "video/webm"
              : "video/mp4",
          });
    const objectUrl = URL.createObjectURL(typed);
    clearVideoSource(video);
    video.src = objectUrl;
    video.load();
    try {
      video.muted = true;
      void video.play().catch(() => {});
    } catch {
      /* */
    }
    log("Progressive blob готов");
    return { objectUrl };
  } catch (e) {
    const name = (e as { name?: string })?.name || "";
    log(name === "AbortError" ? "Таймаут progressive" : "Ошибка progressive");
    return { error: name === "AbortError" ? "timeout" : "fetch" };
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Try direct src first, then blob. Returns objectUrl only when blob path used.
 */
export async function loadProgressiveSource(
  video: HTMLVideoElement,
  url: string,
  onLog?: SimpleLog,
): Promise<{ objectUrl: string | null } | { error: string }> {
  const log = onLog || (() => {});
  const playUrl = toSameOriginMediaUrl(url) || url;

  // HEAD/GET probe — skip obvious 404 quickly
  try {
    const probe = await fetch(playUrl, {
      method: "GET",
      headers: { Range: "bytes=0-0" },
      credentials: "same-origin",
    });
    if (probe.status === 404 || probe.status === 403) {
      log("Progressive недоступен");
      return { error: `HTTP ${probe.status}` };
    }
  } catch {
    /* continue — some hosts reject Range */
  }

  const waitReady = (timeoutMs = 12000) =>
    new Promise<"ok" | "error">((resolve) => {
      let settled = false;
      const done = (v: "ok" | "error") => {
        if (settled) return;
        settled = true;
        video.removeEventListener("loadeddata", onOk);
        video.removeEventListener("canplay", onOk);
        video.removeEventListener("error", onErr);
        window.clearTimeout(timer);
        resolve(v);
      };
      const onOk = () => {
        if (video.videoWidth >= 2 || video.readyState >= 2) done("ok");
      };
      const onErr = () => done("error");
      const timer = window.setTimeout(() => {
        if (video.videoWidth >= 2 || video.readyState >= 2) done("ok");
        else done("error");
      }, timeoutMs);
      video.addEventListener("loadeddata", onOk);
      video.addEventListener("canplay", onOk);
      video.addEventListener("error", onErr);
      if (video.videoWidth >= 2 || video.readyState >= 2) done("ok");
    });

  await loadVideoDirect(video, playUrl, onLog);
  const directReady = await waitReady(10000);
  if (directReady === "ok") {
    return { objectUrl: null };
  }

  const blob = await loadVideoViaBlob(video, playUrl, onLog);
  if ("error" in blob) return blob;
  const blobReady = await waitReady(15000);
  if (blobReady === "ok") return { objectUrl: blob.objectUrl };
  try {
    URL.revokeObjectURL(blob.objectUrl);
  } catch {
    /* */
  }
  return { error: "decode" };
}
